// Pure helpers + types for insurance form state. Extracted from
// InsuranceFormFields.tsx so that file only exports its component
// (satisfies react-refresh rules: non-component exports in a component
// file break Fast Refresh boundaries).
//
// Category drives shape. category=insurance uses insuranceTypes
// (multi-select); category=licence or accreditation uses subjectName. Name
// is server-derived from category + types/subjectName and is never edited
// directly by the user.

import type {
  CreateInsuranceInput,
  EvidenceLink,
  Insurance,
  InsuranceCategory,
  InsuranceType,
} from "../../../../types";
import { DEFAULT_REMINDER_DAYS } from "../../../../types";
import { cleanEvidenceLinks } from "../../../../utils/evidenceLinks";
import { isValidIsoDate } from "../shared/isoDateValidation";

export interface InsuranceFormState {
  category: InsuranceCategory;
  insuranceTypes: InsuranceType[];
  subjectName: string;
  provider: string;
  policyNumber: string;
  /** Whole AUD dollars. `0` is treated as "unset" and omitted on save. */
  coverageValue: number;
  /** Free-text contract clause reference. Optional, all categories. */
  contractClause: string;
  /** Minimum coverage required by the contract (whole AUD). Insurance-only.
   *  `0` is treated as "unset" and omitted on save (mirrors coverageValue). */
  minimumLimit: number;
  /** Free-text name. Required when insuranceTypes includes 'other'. */
  otherInsuranceName: string;
  /** State / jurisdiction. Required when insuranceTypes includes
   *  'workers-compensation' (workers comp is state-regulated in AU). */
  workersCompensationState: string;
  startDate: string;
  expiryDate: string;
  reminderDays: number[];
  evidenceLinks: EvidenceLink[];
  notes: string;
  renewedFromId?: string;
}

export type InsuranceFieldErrors = Partial<
  Record<keyof InsuranceFormState | "form", string>
>;

export function buildEmptyInsuranceFormState(): InsuranceFormState {
  return {
    category: "insurance",
    insuranceTypes: [],
    subjectName: "",
    provider: "",
    policyNumber: "",
    coverageValue: 0,
    contractClause: "",
    minimumLimit: 0,
    otherInsuranceName: "",
    workersCompensationState: "",
    startDate: "",
    expiryDate: "",
    reminderDays: [...DEFAULT_REMINDER_DAYS],
    evidenceLinks: [],
    notes: "",
    renewedFromId: undefined,
  };
}

export function stateFromInsurance(item: Insurance): InsuranceFormState {
  return {
    category: item.category,
    insuranceTypes: item.insuranceTypes ? [...item.insuranceTypes] : [],
    subjectName: item.subjectName ?? "",
    provider: item.provider,
    policyNumber: item.policyNumber ?? "",
    coverageValue: item.coverageValue ?? 0,
    contractClause: item.contractClause ?? "",
    minimumLimit: item.minimumLimit ?? 0,
    otherInsuranceName: item.otherInsuranceName ?? "",
    workersCompensationState: item.workersCompensationState ?? "",
    startDate: item.startDate,
    expiryDate: item.expiryDate,
    reminderDays: [...item.reminderDays],
    evidenceLinks: item.evidenceLinks ? [...item.evidenceLinks] : [],
    notes: item.notes ?? "",
  };
}

/** Add `days` to a YYYY-MM-DD date. Normalises month/year rollover. */
function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

/** Coverage periods shorter than this (in days) trip the soft short-coverage
 *  note — these compliance records are overwhelmingly multi-month-to-multi-year,
 *  so anything shorter is almost always a date-entry slip (e.g. a renewal whose
 *  expiry year wasn't bumped). Strict `<`: an exact 30-day period is not short. */
export const SHORT_COVERAGE_DAYS = 30;

/** Whole days between two YYYY-MM-DD dates (expiry − start), UTC-anchored. */
function daysBetween(startIso: string, expiryIso: string): number {
  const [sy, sm, sd] = startIso.split("-").map(Number);
  const [ey, em, ed] = expiryIso.split("-").map(Number);
  return Math.round(
    (Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd)) / 86_400_000,
  );
}

/**
 * A non-blocking note when the entered coverage period is suspiciously short
 * (under {@link SHORT_COVERAGE_DAYS}), or `null` when there is nothing to flag.
 * Returns null unless BOTH dates are valid ISO and expiry ≥ start — an invalid
 * date or expiry-before-start is a real validation error surfaced elsewhere, so
 * the note never competes with it. Purely advisory: the caller renders it as a
 * caption and does NOT block submission, so legitimately-short policies (event
 * cover, bridging policies) still save.
 */
export function shortCoverageNote(
  startDate: string,
  expiryDate: string,
): string | null {
  if (!isValidIsoDate(startDate) || !isValidIsoDate(expiryDate)) return null;
  const days = daysBetween(startDate, expiryDate);
  if (days < 0 || days >= SHORT_COVERAGE_DAYS) return null;
  if (days === 0) {
    return "Start and expiry are the same day — double-check the dates.";
  }
  return `Coverage is only ${days} day${days === 1 ? "" : "s"} — double-check the dates.`;
}

export function stateFromRenewSource(item: Insurance): InsuranceFormState {
  return {
    ...stateFromInsurance(item),
    // Renewal takes effect the day after the old policy lapses — avoids a
    // one-day double-cover overlap that the prior `startDate = expiryDate`
    // default produced.
    startDate: addDays(item.expiryDate, 1),
    expiryDate: "",
    // Identity-bound: the new policy gets its own number from the insurer;
    // never inherit the old one.
    policyNumber: "",
    evidenceLinks: [],
    notes: "",
    renewedFromId: item.id,
  };
}

/**
 * Convert form state into the Create input shape the backend expects.
 * Omits optional fields when blank so the backend doesn't persist blanks.
 * Name is server-derived — never sent from the UI. `coverageValue === 0`
 * is the "unset" sentinel (CurrencyInput stores whole dollars; a 0-value
 * policy has no business meaning).
 */
export function toCreateInsuranceInput(
  state: InsuranceFormState,
  contractId: string,
): CreateInsuranceInput {
  const base: CreateInsuranceInput = {
    contractId,
    category: state.category,
    provider: state.provider.trim(),
    startDate: state.startDate,
    expiryDate: state.expiryDate,
    reminderDays: state.reminderDays,
  };
  if (state.category === "insurance") {
    base.insuranceTypes = [...state.insuranceTypes];
  } else {
    base.subjectName = state.subjectName.trim();
  }
  if (state.policyNumber.trim()) base.policyNumber = state.policyNumber.trim();
  if (state.coverageValue > 0) base.coverageValue = state.coverageValue;
  if (state.contractClause.trim()) {
    base.contractClause = state.contractClause.trim();
  }
  if (state.category === "insurance" && state.minimumLimit > 0) {
    base.minimumLimit = state.minimumLimit;
  }
  if (
    state.category === "insurance" &&
    state.insuranceTypes.includes("other") &&
    state.otherInsuranceName.trim()
  ) {
    base.otherInsuranceName = state.otherInsuranceName.trim();
  }
  if (
    state.category === "insurance" &&
    state.insuranceTypes.includes("workers-compensation") &&
    state.workersCompensationState.trim()
  ) {
    base.workersCompensationState = state.workersCompensationState.trim();
  }
  // Drop untouched-default / blank evidence rows on save (validation in the
  // modal has already rejected user-labelled rows missing a URL or with an
  // unsafe scheme, so by here `error` is null).
  const cleanedEvidence = cleanEvidenceLinks(state.evidenceLinks).kept;
  if (cleanedEvidence.length > 0) base.evidenceLinks = cleanedEvidence;
  if (state.notes.trim()) base.notes = state.notes.trim();
  if (state.renewedFromId) base.renewedFromId = state.renewedFromId;
  return base;
}
